import { useDisclosure } from "@mantine/hooks";
import { ColumnDef } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { DeleteMaterialsConfirmationModal } from "#modules/company/view/DeleteMaterialsConfirmationModal";
import { makeVolumetricMaterialsCsv } from "#modules/materials/domain/makeVolumetricMaterialsCsv";
import { formatErrorWithLocation } from "#modules/materials/helpers/materialErrorFormatter";
import { MaterialsVolumetricPriceDto } from "#modules/materials/infra/fetchMaterialsVolumetricPrice.api";
import { useCreateMaterialsVolumetricPrice } from "#modules/materials/view/useCreateMaterialsVolumetricPrice";
import { useDeleteAllVolumetricMaterials } from "#modules/materials/view/useDeleteAllVolumetricMaterials";
import { useDeleteMaterialVolumetricPrice } from "#modules/materials/view/useDeleteMaterialVolumetricPrice";
import { useMaterialsVolumetricPrice } from "#modules/materials/view/useMaterialsVolumetricPrice";
import { useUpdateMaterialsVolumetricPrice } from "#modules/materials/view/useUpdateMaterialsVolumetricPrice";
import { useUploadVolumetricBaseMaterials } from "#modules/materials/view/useUploadVolumetricBaseMaterials";
import { makeDownloadableUrl } from "#shared/helpers/file";
import { Icon } from "#shared/icons/Icon";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { Alert } from "#shared/view/components/Alert";
import { Anchor } from "#shared/view/components/Anchor";
import { Box } from "#shared/view/components/Box";
import { EditableTable } from "#shared/view/components/EditableTable/EditableTable";
import { EditableTableCell } from "#shared/view/components/EditableTable/EditableTableCell";
import { Flex } from "#shared/view/components/Flex";
import { Group } from "#shared/view/components/Group";
import { Space } from "#shared/view/components/Space";
import { Button } from "#shared/view/designSystem/Button";
import { FileButton } from "#shared/view/designSystem/FileButton";
import { formatPrice, MaterialGradeSchema, MaterialGradeType, MaterialUploadError } from "shared";

const materialGrades = Object.values(MaterialGradeSchema.enum);

const MaterialsVolumetricPriceTable = () => {
  const { t } = useTranslation();
  const { materialsVolumetricPrice } = useMaterialsVolumetricPrice();

  const { createMaterialsVolumetricPrice } = useCreateMaterialsVolumetricPrice();

  const { updateMaterialsVolumetricPrice } = useUpdateMaterialsVolumetricPrice();

  const { deleteMaterialVolumetricPrice } = useDeleteMaterialVolumetricPrice();
  const { deleteAllVolumetricMaterials } = useDeleteAllVolumetricMaterials();

  const [uploadErrors, setUploadErrors] = useState<MaterialUploadError[]>([]);
  const importButtonRef = useRef<() => void>(null);
  const { uploadVolumetricBaseMaterials } = useUploadVolumetricBaseMaterials((errors) =>
    setUploadErrors(errors),
  );

  const [
    isDeleteConfirmationModalOpened,
    { open: openDeleteConfirmationModal, close: closeDeleteConfirmationModal },
  ] = useDisclosure(false);

  const handleFileUpload = (file: File | null) => {
    if (!file) {
      return;
    }
    setUploadErrors([]);
    uploadVolumetricBaseMaterials(file);
    importButtonRef.current?.();
  };

  const makeCsvUrl = () => {
    const csvData = makeVolumetricMaterialsCsv(materialsVolumetricPrice ?? []);
    return makeDownloadableUrl(csvData, "text/csv");
  };

  const downloadButtonLabel =
    (materialsVolumetricPrice?.length ?? 0) === 0
      ? t("settings.materials.upload.downloadTemplate")
      : t("settings.materials.upload.downloadFile");

  const onUpdateCell = (rowId: string, columnId: string, value: unknown) => {
    const currentMaterialVolumetricPrice = materialsVolumetricPrice?.find(
      (materialVolumetricPrice) => materialVolumetricPrice.id === rowId,
    );

    if (!currentMaterialVolumetricPrice) {
      return;
    }

    const newMaterialsVolumetricPrice = {
      ...currentMaterialVolumetricPrice,
      [columnId]: value,
    };

    updateMaterialsVolumetricPrice(newMaterialsVolumetricPrice);
  };

  const onAddRow = () => {
    createMaterialsVolumetricPrice({
      materialGrade: MaterialGradeSchema.enum.OTHER,
      volumetricPrice: 0,
      name: "",
    });
  };

  const defaultColumn: Partial<ColumnDef<MaterialsVolumetricPriceDto[number]>> = {
    cell: ({ getValue, row, column: { id }, table }) => (
      <EditableTableCell
        initialValue={getValue()}
        rowId={row.id}
        columnId={String(id)}
        table={table}
      />
    ),
  };

  const columns = useMemo<ColumnDef<MaterialsVolumetricPriceDto[number]>[]>(
    () => [
      {
        accessorKey: "materialGrade",
        header: t("qrl.materialGrade"),
        cell: ({ getValue, row, table }) => (
          <EditableTableCell
            initialValue={getValue()}
            rowId={row.id}
            columnId="materialGrade"
            type="select"
            table={table}
            formatDisplayValue={(value) =>
              `${t(`qrl.materialGrades.${value as MaterialGradeType}`)}`
            }
            options={materialGrades
              .map((grade) => ({
                value: grade,
                label: t(`qrl.materialGrades.${grade}`),
              }))
              .sort((a, b) => a.label.localeCompare(b.label))}
          />
        ),
      },
      {
        accessorKey: "name",
        header: t("settings.materials.name"),
      },
      {
        accessorKey: "volumetricPrice",
        header: `${t("settings.materials.volumetricPrice")} €/kg`,
        cell: ({ getValue, row, table }) => (
          <EditableTableCell
            initialValue={getValue()}
            rowId={row.id}
            columnId="volumetricPrice"
            type="price"
            table={table}
            suffix="€/kg"
            formatDisplayValue={(value) =>
              `${formatPrice(typeof value === "number" ? value : 0)}/kg`
            }
          />
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <Group gap="xs" justify="end">
            <ActionIcon
              onClick={() => deleteMaterialVolumetricPrice(row.id)}
              variant="subtle"
              size="sm"
              aria-label={`Remove row ${row.index}`}
            >
              <Icon name="trash" size={14} />
            </ActionIcon>
          </Group>
        ),
        meta: { width: "5%" },
      },
    ],
    [t, deleteMaterialVolumetricPrice],
  );

  const sortedMaterialsVolumetricPrice = useMemo(() => {
    return [...(materialsVolumetricPrice ?? [])].sort((a, b) => {
      const materialGradeComparison = t(`qrl.materialGrades.${a.materialGrade}`).localeCompare(
        t(`qrl.materialGrades.${b.materialGrade}`),
      );

      if (materialGradeComparison === 0) {
        return a.name.localeCompare(b.name);
      }

      return materialGradeComparison;
    });
  }, [materialsVolumetricPrice, t]);

  return (
    <Box miw={400} maw={800} w="70%">
      {uploadErrors.length > 0 && (
        <>
          <Alert
            color="red"
            icon={<Icon name="alertTriangle" />}
            title={t("settings.materials.upload.validationErrors")}
            withCloseButton
            onClose={() => setUploadErrors([])}
          >
            {uploadErrors.slice(0, 10).map((error, index) => (
              <div key={index}>{formatErrorWithLocation(error, t)}</div>
            ))}
          </Alert>
          <Space h="sm" />
        </>
      )}
      <Flex mb="sm" gap={"sm"}>
        <FileButton resetRef={importButtonRef} onChange={handleFileUpload} accept="text/csv">
          {(props) => (
            <Button variant="outline" icon={<Icon name="upload" />} {...props}>
              Importer un fichier
            </Button>
          )}
        </FileButton>
        <Anchor
          href={makeCsvUrl()}
          target="_blank"
          rel="noopener noreferrer"
          download={`export-fabera-volumetric-${DateTime.now().toISODate()}.csv`}
        >
          <Button variant="outline" icon={<Icon name="download" />}>
            {downloadButtonLabel}
          </Button>
        </Anchor>
      </Flex>
      {(materialsVolumetricPrice?.length ?? 0) > 0 && (
        <Group mb="sm">
          <Button
            variant="light"
            color="red"
            icon={<Icon name="trash" />}
            onClick={openDeleteConfirmationModal}
          >
            {t("settings.materials.deleteAllMaterials")}
          </Button>
        </Group>
      )}
      <EditableTable
        data={sortedMaterialsVolumetricPrice}
        columns={columns}
        defaultColumn={defaultColumn}
        getRowId={(row) => row.id}
        onUpdateCell={onUpdateCell}
        onAddRow={onAddRow}
        pageSize={10}
      />
      <DeleteMaterialsConfirmationModal
        opened={isDeleteConfirmationModalOpened}
        onClose={closeDeleteConfirmationModal}
        onDelete={deleteAllVolumetricMaterials}
      />
    </Box>
  );
};

export default MaterialsVolumetricPriceTable;
